import { useMemo, useState, type MouseEvent } from 'react';
import type { Conversation, User } from '../api';
import { UserMenu } from './UserMenu';
import { Mascot } from './Mascot';
import './Sidebar.css';

interface SidebarProps {
  user: User;
  conversations: Conversation[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onCreate: () => void;
  onRename: (id: string, title: string) => void;
  onClone: (id: string) => void;
  onLogout: () => void;
  mobileOpen: boolean;
  onClose: () => void;
}

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

function formatTime(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  if (isToday) {
    return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  }
  return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
}

export function Sidebar({
  user,
  conversations,
  activeId,
  onSelect,
  onCreate,
  onRename,
  onClone,
  onLogout,
  mobileOpen,
  onClose,
}: SidebarProps) {
  const [query, setQuery] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');

  const startEdit = (conv: Conversation, e: MouseEvent) => {
    e.stopPropagation();
    setEditingId(conv.id);
    setEditValue(conv.title);
  };

  const commitEdit = (conv: Conversation) => {
    setEditingId(null);
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== conv.title) {
      onRename(conv.id, trimmed);
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return conversations;
    return conversations.filter(
      (c) => c.title.toLowerCase().includes(q) || c.ticketKey?.toLowerCase().includes(q)
    );
  }, [conversations, query]);

  const recentTickets = useMemo(() => {
    const cutoff = Date.now() - WEEK_MS;
    return conversations
      .filter((c) => c.status === 'created' && new Date(c.updatedAt).getTime() >= cutoff)
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
      .slice(0, 5);
  }, [conversations]);

  return (
    <>
      <div
        className={`sidebar-overlay ${mobileOpen ? 'open' : ''}`}
        onClick={onClose}
      />
      <aside className={`sidebar ${mobileOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-brand">
            <div className="sidebar-logo">
              <Mascot expression="idle" size={30} />
            </div>
            <div className="sidebar-brand-text">
              <h2>BK Agent</h2>
              <span className="sidebar-subtitle">Asistente de tickets Jira</span>
            </div>
          </div>
          <UserMenu user={user} onLogout={onLogout} />
        </div>

        <div className="sidebar-toolbar">
          <button className="new-conversation-button" onClick={onCreate}>
            <span className="new-conversation-icon">+</span>
            <span>Nueva conversación</span>
          </button>
        </div>

        {recentTickets.length > 0 && (
          <div className="recent-tickets">
            <span className="recent-tickets-label">
              {recentTickets.length === 1 ? '1 ticket esta semana' : `${recentTickets.length} tickets esta semana`}
            </span>
            <div className="recent-tickets-strip">
              {recentTickets.map((conv) => (
                <button
                  key={conv.id}
                  type="button"
                  className="recent-ticket-chip"
                  onClick={() => {
                    onSelect(conv.id);
                    onClose();
                  }}
                  title={conv.title}
                >
                  {conv.ticketKey}
                </button>
              ))}
            </div>
          </div>
        )}

        {conversations.length > 0 && (
          <div className="sidebar-search">
            <span className="sidebar-search-icon">🔍</span>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar conversación o ticket..."
              aria-label="Buscar conversación"
            />
          </div>
        )}

        <div className="sidebar-list">
          {conversations.length === 0 && (
            <div className="empty">
              <span className="empty-icon">💬</span>
              <div>No hay conversaciones todavía</div>
              <p>Crea la primera para empezar a redactar un ticket.</p>
            </div>
          )}
          {conversations.length > 0 && filtered.length === 0 && (
            <div className="empty">
              <span className="empty-icon">🔍</span>
              <div>Sin resultados para "{query}"</div>
            </div>
          )}
          {filtered.map((conv, index) => (
            <div
              key={conv.id}
              role="button"
              tabIndex={0}
              className={`conversation-item ${conv.id === activeId ? 'active' : ''}`}
              onClick={(e) => {
                if (editingId === conv.id) return;
                if ((e.target as HTMLElement).closest('button, a, input')) return;
                onSelect(conv.id);
                onClose();
              }}
              onKeyDown={(e) => {
                if (editingId === conv.id) return;
                if ((e.target as HTMLElement).closest('button, a, input')) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelect(conv.id);
                  onClose();
                }
              }}
              style={{ animationDelay: `${Math.min(index, 12) * 35}ms` }}
            >
              <div className={`conversation-icon ${conv.status}`}>
                {conv.status === 'created' ? '✓' : '💬'}
              </div>
              <div className="conversation-body">
                <div className="conversation-header">
                  {editingId === conv.id ? (
                    <input
                      className="conversation-title-input"
                      value={editValue}
                      autoFocus
                      onChange={(e) => setEditValue(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === 'Enter') commitEdit(conv);
                        if (e.key === 'Escape') setEditingId(null);
                      }}
                      onBlur={() => commitEdit(conv)}
                    />
                  ) : (
                    <div className="conversation-title">{conv.title}</div>
                  )}
                  <div className="conversation-header-right">
                    {editingId !== conv.id && (
                      <>
                        <button
                          type="button"
                          className="conversation-edit-button"
                          aria-label="Clonar conversación"
                          title="Clonar conversación"
                          onClick={(e) => {
                            e.stopPropagation();
                            onClone(conv.id);
                          }}
                        >
                          📋
                        </button>
                        <button
                          type="button"
                          className="conversation-edit-button"
                          aria-label="Editar nombre"
                          onClick={(e) => startEdit(conv, e)}
                        >
                          ✏️
                        </button>
                      </>
                    )}
                    <div className="conversation-time">{formatTime(conv.updatedAt)}</div>
                  </div>
                </div>
                <div className="conversation-meta">
                  <span className={`status-badge ${conv.status}`}>
                    <span className="status-dot" />
                    {conv.status === 'created' ? 'Ticket creado' : 'En refinamiento'}
                  </span>
                  {conv.ticketKey && (
                    <a
                      href={conv.ticketUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ticket-key"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {conv.ticketKey}
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </aside>
    </>
  );
}
